import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { InconsistenciaEnderecoConverter } from 'src/app/models/converters/inconsistencia-endereco.converters';
import { InconsistenciaEnderecoForm } from 'src/app/models/inconsistencia-endereco/InconsistenciaEnderecoForm';
import { InconsistenciaEnderecoResponse } from 'src/app/models/inconsistencia-endereco/InconsistenciaEnderecoResponse';
import { InconsistenciaEnderecoView } from 'src/app/models/inconsistencia-endereco/InconsistenciaEnderecoView';
import { TratativaEnderecoService } from 'src/app/services/tratativa-endereco.service';
import { GoogleComponent } from '@pages/utils/google.component';
import { TagManagerDirective } from '../../directives/tag-manager.directive';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CamelCasePipe } from '../../pipes/camel-case.pipe';

@Component({
    selector: 'app-tratativas-enderecos',
    templateUrl: './tratativas-enderecos.component.html',
    styleUrls: ['./tratativas-enderecos.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TagManagerDirective, NgClass, FormsModule, CamelCasePipe]
})
export class TratativasEnderecosComponent extends GoogleComponent implements OnInit, OnDestroy {
  private readonly LS_KEY_INCONSISTENCIA = 'inconsist-end';
  private readonly idImportacao: any;
  private readonly idEmpresaLogada: number;
  public inconsistenciasEnderecos: InconsistenciaEnderecoView[];
  public enderecosCadastrados: any = null;
  public enviandoParaApi = {};
  public erroEnvioParaApi = false;
  public sucessoEnvioApi = {};
  public statusDepara = '0%';
  inconsistenciasAbertas = {};
  subscriptions: Array<Subscription> = [];
  public loadingEnderecos: boolean;
  public listaEnderecos: any = [];

  private readonly sectionApp = 'tratativas';
  private readonly screenName = 'tratativas:manutencao-de-endereco';
  private readonly screenType = 'pagina';

  constructor(private readonly serviceTratativas: TratativaEnderecoService) {
    super();
    this.idEmpresaLogada = localStorage.idEmpresaLogada;
    this.idImportacao = localStorage.getItem('idImportacao');
  }

  ngOnInit(): void {
    this.consultaInconsistenciasEndereco();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  addTagManagerInteraction(elementType: string, elementContent: string) {
    return this.buildTagInteraction(elementType, elementContent, this.sectionApp, this.screenName, this.screenType);
  }

  consultaInconsistenciasEndereco() {
    this.inconsistenciasEnderecos = null;
    this.serviceTratativas.qtdeInconsistenciasEnderecos = 0;
    this.serviceTratativas.changeQtdeInconEnd.emit(0);
    this.erroEnvioParaApi = false;
    this.statusDepara = '0%';
    this.serviceTratativas.getInconsistencias(this.idImportacao).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.inconsistenciasEnderecos = [];
        if (response.length > 0) {
          const listInconsistenciasView: Array<InconsistenciaEnderecoView> = [];
          response.map((inconsistencia: InconsistenciaEnderecoResponse) => {
            const inconsistenciaMapped = InconsistenciaEnderecoConverter.inconsistenciaResponseToInconsistenciaView(
              inconsistencia, response.indexOf(inconsistencia)
            );
            if (inconsistenciaMapped.processado == 'N' || inconsistenciaMapped.processado == 'E') {
              this.serviceTratativas.qtdeInconsistenciasEnderecos++;
              this.serviceTratativas.changeQtdeInconEnd.emit(this.serviceTratativas.qtdeInconsistenciasEnderecos);
            }
            listInconsistenciasView.push(inconsistenciaMapped);
          });
          this.fillLSInconsistencias(listInconsistenciasView);
          this.updateInconsistenciasView();
        } else {
          this.serviceTratativas.qtdeInconsistenciasEnderecos = 0;
          this.serviceTratativas.changeQtdeInconEnd.emit(0);
        }
      },
      error: (erro: any) => {
        console.error(erro);
        this.inconsistenciasEnderecos = undefined;
        this.serviceTratativas.qtdeInconsistenciasEnderecos = 0;
        this.serviceTratativas.changeQtdeInconEnd.emit(0);
      }
    });
  }

  consultaEnderecosCadastrados(inconsistencia: any) {
    this.loadingEnderecos = true;
    this.serviceTratativas.getEnderecosCadastrados(inconsistencia.idEmpresa).pipe(take(1)).subscribe({
      next: (response: any) => {
        if (response.status == 'OK') {
          this.enderecosCadastrados = response.response;
          this.adicionaEnderecoLista(inconsistencia);
          this.loadingEnderecos = false;
        } else {
          this.enderecosCadastrados = false;
        }
      },
      error: (erro: any) => {
        this.enderecosCadastrados = false;
        console.log(erro);
      }
    });
  }

  adicionaEnderecoLista(inconsistencia: any) {
    const index = this.listaEnderecos.findIndex(x => x.enderecosCadastrados[0].numeroLinha === inconsistencia.numeroLinha);
    if (index < 0) {
      this.listaEnderecos.push({
        enderecosCadastrados: [{ numeroLinha: inconsistencia.numeroLinha, enderecos: { listaEnderecos: this.enderecosCadastrados } }]
      });
    }
    this.listaEnderecos.sort((a, b) => a.enderecosCadastrados[0].numeroLinha - b.enderecosCadastrados[0].numeroLinha);
  }

  abreFecharResolucao(inconsistencia: any) {
    this.inconsistenciasAbertas[inconsistencia.numeroLinha] = !this.inconsistenciasAbertas[inconsistencia.numeroLinha];
  }

  resolveInconsistencia(objResolvido: InconsistenciaEnderecoView) {
    this.enviandoParaApi[objResolvido.numeroLinha] = true;
    this.erroEnvioParaApi = false;
    const objResolvidoConverted: InconsistenciaEnderecoForm = InconsistenciaEnderecoConverter.inconsistenciaViewToInconsistenciaForm(objResolvido);
    const body = new FormData();
    body.append('company-id', `${this.idEmpresaLogada}`);
    Object.entries(objResolvidoConverted).forEach((arrayObject) => body.append(arrayObject[0], arrayObject[1]));

    this.serviceTratativas.postDepara(body, this.idImportacao).subscribe({
      next: () => {
        this.enviandoParaApi[objResolvido.numeroLinha] = false;
        this.erroEnvioParaApi = false;
        this.sucessoEnvioApi[objResolvido.numeroLinha] = true;
        this.inconsistenciasAbertas[objResolvido.numeroLinha] = false;
        this.serviceTratativas.qtdeInconsistenciasEnderecos--;
        this.serviceTratativas.changeQtdeInconEnd.emit(this.serviceTratativas.qtdeInconsistenciasEnderecos);
        objResolvido.processado = 'S';
        this.updateInconsistenciaProcessada(objResolvido);
        this.updateInconsistenciasView();
      },
      error: (erro: any) => {
        this.enviandoParaApi[objResolvido.numeroLinha] = false;
        this.erroEnvioParaApi = true;
        objResolvido.processado = 'E';
        this.updateInconsistenciaProcessada(objResolvido);
        console.error(erro);
      }
    });
  }

  alterouEndereco(descricaoEndereco: string, inconsistencia) {
    const enderecoCompleto = this.listaEnderecos[inconsistencia.numeroLinha]
      .enderecosCadastrados[0].enderecos.listaEnderecos
      .find((endereco: any) => {
        const descricao = `${endereco.idEndereco.descricaoLogradouro},${endereco.idEndereco.codigoNumero}`;
        return descricao == descricaoEndereco;
      });
    inconsistencia.novoIdEndereco = enderecoCompleto.idEndereco.idEndereco;
  }

  getInconsistenciasLocalStorage(): Array<InconsistenciaEnderecoView> {
    return JSON.parse(localStorage.getItem(this.LS_KEY_INCONSISTENCIA)) as Array<InconsistenciaEnderecoView>;
  }

  updateInconsistenciaProcessada(inconsistencia: InconsistenciaEnderecoView) {
    const listInconsistencias = this.getInconsistenciasLocalStorage();
    const oldInconsistIndex = listInconsistencias.findIndex(
      (item) => inconsistencia.idImportacao == item.idImportacao && inconsistencia.cnpj == item.cnpj && inconsistencia.numeroLinha == item.numeroLinha
    );
    const newList = [
      ...listInconsistencias.slice(0, oldInconsistIndex),
      ...listInconsistencias.slice(oldInconsistIndex + 1),
    ];
    newList.push(inconsistencia);
    newList.sort((a, b) => a.numeroLinha - b.numeroLinha);
    this.listaEnderecos.slice(oldInconsistIndex, 1);
    this.fillLSInconsistencias(newList);
  }

  fillLSInconsistencias(inconsistenciasList: Array<InconsistenciaEnderecoView>) {
    localStorage.setItem(this.LS_KEY_INCONSISTENCIA, JSON.stringify(inconsistenciasList));
  }

  updateInconsistenciasView() {
    this.inconsistenciasEnderecos = this.getInconsistenciasLocalStorage();
    this.inconsistenciasEnderecos.forEach(inconsistencia => this.consultaEnderecosCadastrados(inconsistencia));
  }
}
